import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import type { ImageShowcaseProps, ShowcaseImage } from '@/types/components';

const defaultImages: ShowcaseImage[] = [
  {
    url: "https://res.cloudinary.com/dqql2wlbt/image/upload/v1738001876/3d-repo-gene_oo91up.webp?q_auto",
    alt: "Repo Gene 3D Cube"
  },
  {
    url: "https://res.cloudinary.com/dqql2wlbt/image/upload/v1736443377/3d-cube-pulse-gene_r8eq4h.webp?q_auto",
    alt: "Pulse Gene 3D Cube"
  },
  {
    url: "https://res.cloudinary.com/dqql2wlbt/image/upload/v1736442321/3d-clou-gene_rbhxu0.webp?q_auto",
    alt: "ClouGene 3D Cube"
  },
  {
    url: "https://res.cloudinary.com/dqql2wlbt/image/upload/v1738002658/3d-cube-auth-gene_pnqtfi.webp?q_auto",
    alt: "Auth 3D Cube"
  },
  {
    url: "https://res.cloudinary.com/dqql2wlbt/image/upload/v1738001784/scribe-gene-3d-cube_h3060s.webp?q_auto",
    alt: "Route 3D Cube"
  }
];

const GlassCube: React.FC<ImageShowcaseProps> = ({ 
  className,
  images = defaultImages,
  autoRotateInterval = 5000,
  showNavigation = true
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, autoRotateInterval);

    return () => clearInterval(timer);
  }, [images.length, autoRotateInterval]);

  const handleImageChange = (index: number): void => {
    if (index >= 0 && index < images.length) {
      setCurrentIndex(index);
    }
  };

  return (
    <div className={`relative ${className || ''}`}>
      <motion.div
        key={`${currentIndex}-${images[currentIndex].url}`}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ 
          duration: 0.5,
          ease: "easeOut"
        }}
        className="relative w-full h-full"
      >
        {/* Glass effect background */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-emerald-500/10 rounded-3xl backdrop-blur-sm" />
        
        {/* Border animation */}
        <div className="absolute inset-0 rounded-3xl border-2 border-transparent bg-gradient-to-r from-blue-500/50 to-emerald-500/50 mask-border" />
        
        {/* Image */}
        <div className="relative w-full h-full p-4">
          <motion.img
            key={images[currentIndex].url}
            src={images[currentIndex].url}
            alt={images[currentIndex].alt}
            className="w-full h-full object-contain rounded-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </motion.div>

      {/* Navigation dots */}
      {showNavigation && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => handleImageChange(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-blue-500 w-4'
                  : 'bg-gray-400 hover:bg-blue-400'
              }`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              aria-label={`View ${images[index].alt}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default GlassCube;
